import { useEffect, useRef, useState } from "react";
import "./App.css";
import Widget from "./components/widget/widget";
import useToggleWidget from "./store/useToogleWidget";
import {
  applyStoredSettingsOnStartup,
  getHideWidgetStatus,
  getProfileList,
  getwidgetSize,
  hideWidgetStatus,
  isMobile,
  removeTooltipStatus,
  setProfileList,
  usePageStructureSync,
  useVoiceNavSync,
} from "./store/user";
import "./i18n/i18n";
import { useDispatch, useSelector } from "react-redux";
import VoiceNavigation from "./components/voiceNavigation/voiceNavigation";
import { selectIsDictionaryModalOpen } from "./reduxSlices/dictionarySlices";
import Dictionary from "./components/dictionary/dictionary";
import {
  selectIsDictionaryActive,
  selectIsVoiceNavigationActive,
  setDictionaryActive,
  setPageStructureActive,
  setVoiceNavigationActive,
} from "./reduxSlices/booleanSlice";
import { selectIsResetOpen } from "./reduxSlices/resetToggleSlice";
import { setProfileData } from "./reduxSlices/profile";
import { setFeedBackOpened } from "./reduxSlices/feedback";
import { setHideInterfaceStatus } from "./reduxSlices/hideInterfaceSlice";
import { getWidgetSettings } from "./serivces/widgetService";
import { createClickOutsideHandler } from "./ui/clickOutside";
import { useWidgetIconStyle } from "./app/useWidgetIconStyle";

function App() {
  const { isWidgetOpen, openWidget, closeWidget } = useToggleWidget();
  const [definition, setDefinition] = useState<any>();
  const [dictionaryOpen, setDictionaryOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState("");
  const [partsOfSpeech, setPartsOfSpeech] = useState("");
  const [widgetSettings, setWidgetSettings] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const isDictionaryModalOpen = useSelector(selectIsDictionaryModalOpen);
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const isVoiceNavigationActive = useSelector(selectIsVoiceNavigationActive);
  const isDictionaryActive = useSelector(selectIsDictionaryActive);
  const dispatch = useDispatch();
  const isResetOpen = useSelector(selectIsResetOpen);
  const [interfaceHideStatus, setInterfaceHideStatus] = useState(false);
  const hideInterfaceSts = useSelector(
    (state: any) => state.HideInterface.interfaceHide,
  );

  const handleWidgetOpen = () => {
    if (isWidgetOpen) {
      closeWidget();
    } else {
      openWidget();
      setDictionaryOpen(false);
      dispatch(setFeedBackOpened(false));
    }
  };

  useEffect(() => {
    if (getHideWidgetStatus() === true) {
      dispatch(setHideInterfaceStatus(true));
    } else {
      dispatch(setHideInterfaceStatus(false));
    }
  }, [dispatch]);

  useEffect(() => {
    dispatch(setFeedBackOpened(false));
  }, [isWidgetOpen, dispatch]);

  useEffect(() => {
    const profileList = getProfileList();

    if (profileList?.length) {
      for (const p of profileList) {
        if (!p.isSelected) continue;
      }
    }

    setIsLoading(false);
  }, []);

  useEffect(() => {
    const widgetSize = getwidgetSize();

    document.documentElement.style.fontSize = widgetSize ? "125%" : "100%";

    applyStoredSettingsOnStartup();
  }, []);

  useEffect(() => {
    const handleClickOutside = createClickOutsideHandler({
      isOpen: () => isWidgetOpen,
      ref: widgetRef,
      onOutside: () => {
        closeWidget();
      },
    });

    function handleEscapeKey(event: KeyboardEvent) {
      if (!isWidgetOpen) return;
      if (event.key === "Escape") {
        closeWidget();
      }
    }

    if (isWidgetOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isWidgetOpen, closeWidget]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key.toLowerCase() === "u" && !e.altKey) {
        e.preventDefault();
        if (isWidgetOpen) closeWidget();
        else openWidget();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isWidgetOpen, openWidget, closeWidget]);

  useEffect(() => {
    if (isDictionaryModalOpen) {
      window.getSelection()?.empty();
    }
    if (isVoiceNavigationActive || isDictionaryActive) {
      closeWidget();
    }
  }, [
    isDictionaryModalOpen,
    isVoiceNavigationActive,
    isDictionaryActive,
    closeWidget,
  ]);

  useEffect(() => {
    const storedProfile = getProfileList();
    if (storedProfile) {
      for (let i = 0; i < storedProfile.length; i++) {
        if (storedProfile[i].isSelected === true) {
          if (storedProfile[i].title === "Dictionary") {
            dispatch(setDictionaryActive(true));
          }
        }
      }
    }
  }, [dispatch]);

  useEffect(() => {
    const storedProfile = getProfileList();
    if (isResetOpen) {
      dispatch(setVoiceNavigationActive(false));
      dispatch(setDictionaryActive(false));
      dispatch(setPageStructureActive(false));

      removeTooltipStatus();
      localStorage.removeItem("rangeSliderValues");
    }
  }, [isResetOpen, dispatch]);

  const getWidget = async () => {
    const injected = (window as any).__INCLUWAY_BOOTSTRAP__;

    if (injected) {
      setWidgetSettings(injected);
      return;
    }

    const resp = await getWidgetSettings();
    setWidgetSettings(resp);
  };

  const { widgetIconStyle } = useWidgetIconStyle(widgetSettings);

  useEffect(() => {
    getWidget();
  }, []);

  useEffect(() => {
    setInterfaceHideStatus(hideInterfaceSts);
  }, [hideInterfaceSts]);

  useEffect(() => {
    const handleShortcut = (e: KeyboardEvent) => {
      if (e.altKey && e.key.toLowerCase() === "1") {
        e.preventDefault();
        dispatch(setHideInterfaceStatus(false));
        hideWidgetStatus(false);
      }
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, [dispatch]);

  usePageStructureSync(getProfileList(), setProfileData, setProfileList);
  useVoiceNavSync(getProfileList(), setProfileData, setProfileList);

  const isWidgetEnabled =
    (isMobile() && widgetSettings?.advanced?.mobileEnabled) ||
    (!isMobile() && widgetSettings?.advanced?.desktopEnabled);

  const hasResolvedDesktopPosition =
    isMobile() ||
    widgetSettings?.position?.positionOpen?.desktop?.horizontalAnchor ===
      "left" ||
    widgetSettings?.position?.positionOpen?.desktop?.horizontalAnchor ===
      "right";

  return (
    <>
      {isLoading && isWidgetEnabled && (
        <div style={{ display: interfaceHideStatus ? "none" : "block" }}>
          <div
            className="fixed flex items-center justify-center bg-[#fff] rounded-full z-50"
            style={widgetIconStyle}
          >
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-blue-500" />
          </div>
        </div>
      )}

      {!isLoading && dictionaryOpen && !isWidgetOpen && (
        <Dictionary
          word={selectedWord}
          meaning={definition}
          partsOfSpeech={partsOfSpeech}
          onClose={() => setDictionaryOpen(false)}
        />
      )}

      {!isLoading && isVoiceNavigationActive && <VoiceNavigation />}

      <div ref={widgetRef} className="relative">
        {!isLoading &&
          !isWidgetOpen &&
          isWidgetEnabled &&
          !interfaceHideStatus && (
            <button
              className="z-50 flex items-center justify-center shrink-0 aspect-square cursor-pointer rounded-full transition-all duration-300 ease-in-out hover:scale-110 hover:shadow-2xl hover:ring hover:ring-primary/30 hover:-translate-y-1 overflow-hidden"
              onClick={handleWidgetOpen}
              style={{
                ...widgetIconStyle,
                background: widgetSettings?.appearance?.customIcon
                  ? undefined
                  : widgetSettings?.appearance?.colors?.icon?.gradientEnabled
                    ? `linear-gradient(135deg, ${widgetSettings.appearance.colors.icon.color} 0%, ${widgetSettings.appearance.colors.icon.colorGradientEnd} 100%)`
                    : widgetSettings?.appearance?.colors?.icon?.color,
              }}
            >
              {widgetSettings?.appearance?.customIcon ? (
                <img
                  src={widgetSettings.appearance.customIcon}
                  alt="widget icon"
                  className="w-full h-full object-contain rounded-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center p-2">
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 30 21"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.7012 8.73657C14.5635 7.92057 16.7062 5.89597 17.6927 3.42487L14.9435 2.01611C14.3614 3.83287 12.8655 5.3417 10.8118 5.92676C8.75804 6.51182 6.6396 6.02683 5.10333 4.81823L3.62366 7.43559C5.87146 8.98291 8.83081 9.56027 11.6931 8.74427L11.7012 8.73657Z"
                      fill="white"
                    />
                    <path
                      d="M9.60491 4.1108C10.7972 4.1108 11.7638 3.19056 11.7638 2.0554C11.7638 0.920233 10.7972 0 9.60491 0C8.4126 0 7.44604 0.920233 7.44604 2.0554C7.44604 3.19056 8.4126 4.1108 9.60491 4.1108Z"
                      fill="white"
                    />
                    <path
                      d="M7.13324 16.9504L21.1133 3.47095H29.3768L11.265 20.938L7.13324 16.9504Z"
                      fill="white"
                    />
                    <path
                      d="M15.4031 16.9507L8.26352 10.0686H0L11.328 20.9999L15.4031 16.9507Z"
                      fill="white"
                    />
                  </svg>
                </div>
              )}
            </button>
          )}

        {!isLoading &&
          isWidgetEnabled &&
          hasResolvedDesktopPosition &&
          !interfaceHideStatus && <div>{isWidgetOpen && <Widget />}</div>}
      </div>
    </>
  );
}

export default App;
